import apiClient from '@/shared/api/client';
import type {
  AgencyAgentStatus,
  AgentObservationPage,
  AgentRunLogPage,
  AgentTimelineEntry,
} from './agentStatusTypes';
import type { ApiResponse } from '@/shared/api/types';
import type { ReviewRequestsView, TextAnalyticsView } from '@/modules/reviews/analytics/types';
import type {
  AgentContextSource,
  AgentDayOneReport,
  AgentFact,
  AgentSourceHealth,
} from '@/modules/agents/types';

export type NotificationType = 'action' | 'review' | 'fyi';
export type NotificationPriority = 'p0' | 'p1' | 'p2' | 'fyi';
export type NotificationCategory =
  | 'onboarding'
  | 'strategy'
  | 'visibility'
  | 'campaigns'
  | 'alerts';
export type NotificationStatus = 'active' | 'done' | 'dismissed' | 'snoozed';

export type PracticeState =
  | 'zero'
  | 'strategy'
  | 'ads_only'
  | 'seo_only'
  | 'ads_seo'
  | 'active_30d'
  | 'ongoing'
  | 'inactive'
  | 'suspended'
  | 'invited'
  | 'onboarding'
  | 'draft';

// ─── Review Accelerator funnel (PN-internal analytics) ───────────────────────

export type FunnelRange = '7d' | '30d' | '90d';
/** Which invites the review funnel covers: link & QR (default), text, or both mixed. */
export type FunnelChannel = 'manual_qr' | 'sms' | 'all';
export interface ReviewFunnelLocation {
  id: string;
  name: string;
  city: string | null;
}

export type FunnelStepKey =
  | 'scan'
  | 'open'
  | 'rating'
  | 'people'
  | 'reason'
  | 'standout'
  | 'detail'
  | 'own_words'
  | 'expectation'
  | 'draft'
  | 'posted';

export interface FunnelStepRow {
  step: FunnelStepKey;
  label: string;
  count: number;
  eligible: number;
  pctOfScans: number;
  pctOfEligible: number;
  lost: number;
  medianSecondsFromPrev: number | null;
  /** Sessions that rendered this screen — wizard steps only, null on old data. */
  viewed: number | null;
  /** Median seconds from the screen rendering to the patient clearing it. */
  medianDwellSeconds: number | null;
}

export interface ReviewFunnel {
  range: FunnelRange;
  locationId: string | null;
  firstEventAt: string | null;
  steps: FunnelStepRow[];
  branch: {
    fiveStar: number;
    lowStar: number;
    privateFromLowStar: number;
    privateFromHappyPath: number;
  };
  ratingMix: Record<string, number>;
  // `posted` is the tap on "Open Google to paste the review" (the hand-off); `postConfirmed` is
  // the patient telling us afterwards that the review actually went live.
  outcomes: {
    posted: number;
    postConfirmed: number;
    /** New Google reviews matched to these sessions. */
    matched?: number;
    privateFeedback: number;
    abandoned: number;
  };
  biggestDropStep: FunnelStepKey | null;
  weekly: { weekStart: string; scans: number; posted: number }[];
  previous: {
    scans: number;
    rated: number;
    posted: number;
    postConfirmed: number;
    privateFeedback: number;
  };
  locations: ReviewFunnelLocation[];
  drafts: GeneratedDraft[];
}

/**
 * A review draft the tool generated for a patient session, straight from the
 * invite audit log — shown so the agency can read what was actually written,
 * including drafts that never made it to Google.
 */
export interface GeneratedDraft {
  shortCode: string;
  draftText: string;
  archetype: string | null;
  createdAt: string;
  rating: number | null;
  outcome: 'confirmed' | 'handed_off' | 'private' | 'not_posted';
  /**
   * Name the patient gave, when they gave one — required on the private form
   * and on the Google path below 5 stars, never asked of a 5★ patient who taps
   * straight through.
   */
  patientName: string | null;
  /** Staff test session — flagged in the audit view, excluded from the funnel. */
  isTest: boolean;
}

// ─── Agent assignment ─────────────────────────────────────────────────────────

export interface LocationAgentAssignment {
  roleKey: string;
  status: 'active' | 'paused';
  cadenceDays: number;
}

export interface AgentWorkSchedule {
  id: string;
  skillKey: string;
  label: string;
  frequency: string;
  triggerKey: string;
  triggerType: 'check' | 'quota' | 'event';
  /** The event an event trigger listens for (e.g. `gbp.review.updated`). Absent from an older
   *  agent-suite; fall back to `triggerKey`. */
  event?: string | null;
  /** What sends that event, in words. Absent from an older agent-suite or for an event with no
   *  description; show just the event name then. */
  eventLabel?: string | null;
  intervalDaysOverride: number | null;
  quotaCountOverride: number | null;
  minIntervalDays: number | null;
  maxIntervalDays: number | null;
  minimumQuotaCount: number | null;
  quotaPeriod: 'week' | 'month' | null;
  waivedInPeriod: number;
  dueAt: number | null;
  /** When this schedule's slot last finished (approved, reviewed or skipped). Null if never. */
  lastCompletedAt: number | null;
  /** When this skill was paused for this location; null while it runs. */
  pausedAt: number | null;
  /** The weekly ads report only: a snapshot newer than the one its open draft was written from exists. */
  newerData?: { asOf: string; asOfLocal: string };
  activeTaskState:
    | 'ready'
    | 'blocked'
    | 'awaiting_approval'
    | 'in_progress'
    | 'done'
    | 'cancelled'
    | 'failed'
    | 'needs_review'
    | null;
  /** When the open task is deferred to a later time (nobody sees it until then), that time. */
  waitingUntil: number | null;
}

export interface PracticeAgentRow {
  locationId: string;
  locationName: string;
  locationStatus: string;
  /** Which role this row is for. Several roles can be assigned to the SAME location at once, so this
   *  is one row per (location, role) — never assume one row per location. */
  roleKey: string;
  /** Role + persona display data, sourced from agent-suite's on-disk role.json/personas — never
   *  hardcoded in the UI, so a second role just means a second row, not new copy to write. */
  roleDisplayName: string;
  agentName: string;
  /** Whether the practice can see this agent's tasks — the `agentTasks.<role>` rollout flag. Hiring
   *  alone never switches it on (Rollout & Flags does). */
  tasksEnabled: boolean;
  tasksFlag: string;
  rolloutStage: 'general' | 'internal_only';
  practiceInternal: boolean;
  /** Why this agent cannot be switched on for this practice right now, or null. Today only Joe
   *  (community) has one: the Community module must be on first. Both switches show it. */
  hireBlocker: string | null;
  assignment: LocationAgentAssignment | null;
  schedules: AgentWorkSchedule[];
  /** What this location still needs before the agent can genuinely work. Deliberately NOT the
   *  same axis as `assignment.status`: that is the agency's on/off switch, this is whether the
   *  setup behind it is complete. A row can be active and unready at the same time.
   *  Null when nothing is assigned, or when the answer could not be fetched (e.g. agent-suite could
   *  not list the role's requirements). An ASSIGNED row with null renders "Setup unknown" -- never as
   *  ready -- see agents/utils/onboardingDisplay.ts. */
  onboarding: AgentOnboardingStatus | null;
}

/** One requirement and whether this location meets it. Declared with the agent's role. */
export interface AgentRequirementStatus {
  key: string;
  label: string;
  ownedBy: 'backend' | 'agent_suite';
  /** Everyone permitted to resolve it. A Google grant lists only `practice`, so the command
   *  center shows that gap but offers no button that could close it. */
  fixableBy: ReadonlyArray<'agency' | 'practice'>;
  satisfied: boolean;
  /** One plain line on how it stands, when worth showing. The ads connection names each platform
   *  and why it is not ready ("Google Ads: ready · Meta: last sync failed — ..."). */
  detail?: string;
}

export interface AgentOnboardingStatus {
  locationId: string;
  roleKey: string;
  ready: boolean;
  requirements: AgentRequirementStatus[];
}

/** Read-only mission review data. A mission draft is not an AgentTask. */
export type MissionDeadlineStatus = 'on_track' | 'at_risk' | 'overdue' | 'recovered';
export type MissionDeadlineAlertStatus = Exclude<MissionDeadlineStatus, 'on_track'>;

export interface MissionSnapshot {
  /** Real per-source check status, added by the backend proxy. */
  sourceHealth?: AgentSourceHealth[];
  /** The honest list of sources the agent checks beyond the business sources (calendar + feeds). */
  contextSourcesChecked?: AgentContextSource[];
  mission: {
    id: string;
    locationId: string;
    mandate: { statement: string; version?: number };
    goals: Array<{
      id: string;
      statement: string;
      status: 'active' | 'achieved' | 'abandoned';
      priority: number;
      target?: string;
    }>;
    plan: {
      version: number;
      objective: string;
      steps: Array<{
        id: string;
        instruction: string;
        status?: 'pending' | 'active' | 'done' | 'discarded';
        skillKey?: string;
      }>;
      notes?: string;
    };
    scope: {
      locationId: string;
      mode: 'draft-only';
      allowedTools: string[];
      allowedSkills: string[];
    };
    status: 'active' | 'paused' | 'completed' | 'failed';
    revision: number;
    activeRunId?: string;
    activeRunLeaseUntil?: number;
    createdAt: number;
    updatedAt: number;
  } | null;
  tasks: Array<{
    id: string;
    missionId: string;
    locationId: string;
    dedupKey: string;
    goalId?: string;
    title: string;
    intent: unknown;
    prepareAt: number;
    dueAt: number;
    kind: 'standing' | 'opportunity';
    status: 'planned' | 'preparing' | 'awaiting_approval' | 'delivered' | 'cancelled' | 'failed';
    deliveredDraftVersion: number | null;
    deliveryCount: number;
    revision: number;
    createdAt: number;
    updatedAt: number;
  }>;
  drafts: Array<{
    taskId: string;
    version: number;
    idempotencyKey: string;
    body: string;
    evidenceObservationIds: string[];
    sourceRunId?: string;
    createdAt: number;
  }>;
  deadlineStates: Array<{
    id: string;
    missionId: string;
    locationId: string;
    taskId: string;
    logicalKey: string;
    status: MissionDeadlineStatus;
    risk: MissionDeadlineStatus;
    reason?: string;
    dueAt: number;
    prepareAt: number;
    thresholdMs: number;
    remainingMs: number;
    atRiskAt?: number;
    overdueAt?: number;
    ownerRunId?: string;
    evidenceRefreshRequired: boolean;
    evidenceRefreshOwnerRunId?: string;
    evidenceRefreshReason?: string;
    generation: number;
    lastEvaluatedAt: number;
    revision: number;
    createdAt: number;
    updatedAt: number;
  }>;
  deadlineAlerts: Array<{
    id: string;
    missionId: string;
    locationId: string;
    taskId: string;
    logicalKey: string;
    dedupKey: string;
    status: MissionDeadlineAlertStatus;
    reason: string;
    dueAt: number;
    thresholdMs: number;
    remainingMs: number;
    occurredAt: number;
    createdAt: number;
  }>;
  readiness: {
    ready: boolean;
    roleKey: string;
    model: string;
    missing: string[];
    invalid: string[];
    warnings: string[];
    limits: {
      maxIterations: number;
      maxToolCalls: number;
      maxEvidenceAgeMs: number;
      maxEventWaitMs: number;
      deadlineAtRiskWindowMs: number;
    };
  };
  clientReadiness: {
    clientId: string;
    ready: boolean;
    missing: string[];
    warnings: string[];
    connectors: {
      backend: 'configured' | 'missing';
      gbp: 'configured' | 'missing';
    };
  };
}

/** A refusal ("a run is already in progress") arrives as started:false, not as an HTTP error. */
export interface RunAgentResult {
  started: boolean;
  reason?: string;
  /** What actually happened when it is not "started the checks" (Joe queues a scan instead). */
  message?: string;
}

export interface AgentResetResult {
  locationId: string;
  rehiredRoleKeys: string[];
  priorRoleKeys: string[];
  deleted: Record<string, number>;
  /** Agents left alone because they cannot be re-hired right now, with the reason. */
  skipped?: { roleKey: string; reason: string }[];
}

/** The raw notification row returned by the update endpoint. */
export interface AgencyNotification {
  id: string;
  practiceId: string;
  locationId: string | null;
  code: string;
  type: NotificationType;
  priority: NotificationPriority;
  category: NotificationCategory;
  title: string;
  description: string;
  actionLabel: string | null;
  deepLink: string | null;
  status: NotificationStatus;
  snoozedUntil: string | null;
  createdAt: string;
}

export const commandCenterApi = {
  /** The link & QR funnel by default; `channel: 'all'` mixes both (the old behaviour). */
  getPracticeReviewFunnel: async (
    practiceId: string,
    params?: { locationId?: string; range?: FunnelRange; channel?: FunnelChannel },
  ): Promise<ReviewFunnel> => {
    const { data } = await apiClient.get<ApiResponse<ReviewFunnel>>(
      `/agency/command-center/practices/${practiceId}/review-funnel`,
      { params },
    );
    return data.data;
  },

  /** The text funnel: confirmed visit → review on Google, with exits, cost and recent threads. */
  getPracticeTextFunnel: async (
    practiceId: string,
    params?: { locationId?: string; range?: FunnelRange },
  ): Promise<TextAnalyticsView & { locations: ReviewFunnelLocation[] }> => {
    const { data } = await apiClient.get<
      ApiResponse<TextAnalyticsView & { locations: ReviewFunnelLocation[] }>
    >(`/agency/command-center/practices/${practiceId}/review-text-funnel`, { params });
    return data.data;
  },

  /** Review requests across every practice in the agency. */
  getReviewRequests: async (range: FunnelRange): Promise<ReviewRequestsView> => {
    const { data } = await apiClient.get<ApiResponse<ReviewRequestsView>>(
      '/agency/review-requests',
      { params: { range } },
    );
    return data.data;
  },

  setPracticeSnooze: async (
    practiceId: string,
    snoozed: boolean,
  ): Promise<{ id: string; snoozedAt: string | null }> => {
    const { data } = await apiClient.patch<ApiResponse<{ id: string; snoozedAt: string | null }>>(
      `/agency/command-center/practices/${practiceId}/snooze`,
      { snoozed },
    );
    return data.data;
  },

  updateNotification: async (
    id: string,
    update: { status: 'done' | 'dismissed' } | { status: 'snoozed'; snoozedUntil: string },
  ): Promise<AgencyNotification> => {
    const { data } = await apiClient.patch<ApiResponse<AgencyNotification>>(
      `/agency/notifications/${id}`,
      update,
    );
    return data.data;
  },

  // ── Agent assignment (agency-only) ──────────────────────────────────────────
  getPracticeAgents: async (practiceId: string): Promise<PracticeAgentRow[]> => {
    const { data } = await apiClient.get<ApiResponse<PracticeAgentRow[]>>(
      `/agency/command-center/practices/${practiceId}/agents`,
    );
    return data.data;
  },

  /** One agent's composed status and tasks. `all` adds finished tasks. */
  getAgentStatus: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    all = false,
  ): Promise<AgencyAgentStatus> => {
    const { data } = await apiClient.get<ApiResponse<AgencyAgentStatus>>(
      `/agency/command-center/practices/${encodeURIComponent(practiceId)}/agent-status`,
      { params: { locationId, roleKey, ...(all ? { all: '1' } : {}) } },
    );
    return data.data;
  },

  /** One page of the run log, newest first; `before` is the previous page's `nextBefore`. */
  getRunLog: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    before?: number,
  ): Promise<AgentRunLogPage> => {
    const { data } = await apiClient.get<ApiResponse<AgentRunLogPage>>(
      `/agency/command-center/practices/${encodeURIComponent(practiceId)}/run-log`,
      { params: { locationId, roleKey, ...(before ? { before } : {}) } },
    );
    return data.data;
  },

  /** One task's runs, waits, drafts and messages, newest first. */
  getObservations: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    cursor?: string,
  ): Promise<AgentObservationPage> => {
    const { data } = await apiClient.get<ApiResponse<AgentObservationPage>>(
      `/agency/command-center/practices/${encodeURIComponent(practiceId)}/observations`,
      { params: { locationId, roleKey, ...(cursor ? { cursor } : {}) } },
    );
    return data.data;
  },

  getTaskTimeline: async (
    practiceId: string,
    locationId: string,
    taskId: string,
  ): Promise<AgentTimelineEntry[]> => {
    const { data } = await apiClient.get<ApiResponse<{ entries: AgentTimelineEntry[] }>>(
      `/agency/command-center/practices/${encodeURIComponent(practiceId)}/tasks/${encodeURIComponent(taskId)}/timeline`,
      { params: { locationId } },
    );
    return data.data.entries;
  },

  /** Close one open task from its Command Center row (it never blocks its schedule's Run now again). */
  closeTask: async (practiceId: string, locationId: string, taskId: string): Promise<void> => {
    await apiClient.post(
      `/agency/command-center/practices/${encodeURIComponent(practiceId)}/tasks/${encodeURIComponent(taskId)}/close`,
      { locationId },
    );
  },

  /** The client facts at one location: what the practice told its agents, read into every later task. */
  getClientFacts: async (practiceId: string, locationId: string): Promise<AgentFact[]> => {
    const { data } = await apiClient.get<ApiResponse<AgentFact[]>>(
      `/agency/command-center/practices/${encodeURIComponent(practiceId)}/client-facts`,
      { params: { locationId } },
    );
    return data.data;
  },

  /** Removes client facts; the agents stop reading them. */
  retireClientFacts: async (
    practiceId: string,
    locationId: string,
    factIds: string[],
  ): Promise<{ retired: number }> => {
    const { data } = await apiClient.post<ApiResponse<{ retired: number }>>(
      `/agency/command-center/practices/${encodeURIComponent(practiceId)}/client-facts/retire`,
      { locationId, factIds },
    );
    return data.data;
  },

  getPracticeMission: async (
    practiceId: string,
    locationId: string,
    roleKey?: string,
  ): Promise<MissionSnapshot> => {
    const { data } = await apiClient.get<ApiResponse<MissionSnapshot>>(
      `/agency/command-center/practices/${encodeURIComponent(practiceId)}/mission`,
      { params: { locationId, roleKey } },
    );
    return data.data;
  },

  setLocationAgent: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    body: { status?: 'active' | 'paused' },
  ): Promise<LocationAgentAssignment> => {
    const { data } = await apiClient.put<ApiResponse<LocationAgentAssignment>>(
      `/agency/practices/${practiceId}/locations/${locationId}/agents/${roleKey}`,
      body,
    );
    return data.data;
  },

  unassignLocationAgent: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/agency/practices/${practiceId}/locations/${locationId}/agents/${roleKey}`,
    );
  },

  runLocationAgent: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
  ): Promise<RunAgentResult> => {
    const { data } = await apiClient.post<ApiResponse<RunAgentResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/agents/${roleKey}/run`,
      {},
    );
    return data.data;
  },

  updateAgentSchedule: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    scheduleId: string,
    override: { intervalDays?: number | null; quotaCount?: number | null },
  ): Promise<AgentWorkSchedule> => {
    const { data } = await apiClient.patch<ApiResponse<AgentWorkSchedule>>(
      `/agency/practices/${practiceId}/locations/${locationId}/agents/${roleKey}/schedules/${scheduleId}`,
      override,
    );
    return data.data;
  },

  runAgentSchedule: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    scheduleId: string,
  ): Promise<RunAgentResult> => {
    const { data } = await apiClient.post<ApiResponse<RunAgentResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/agents/${roleKey}/schedules/${scheduleId}/run`,
      {},
    );
    return data.data;
  },

  /** Pause or resume a schedule's skill here. Every schedule row of that skill changes together. */
  setAgentSchedulePaused: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    scheduleId: string,
    paused: boolean,
  ): Promise<AgentWorkSchedule[]> => {
    const { data } = await apiClient.post<ApiResponse<AgentWorkSchedule[]>>(
      `/agency/practices/${practiceId}/locations/${locationId}/agents/${roleKey}/schedules/${scheduleId}/pause`,
      { paused },
    );
    return data.data;
  },

  waiveAgentSchedule: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    scheduleId: string,
    reason: string,
  ): Promise<AgentWorkSchedule> => {
    const { data } = await apiClient.post<ApiResponse<AgentWorkSchedule>>(
      `/agency/practices/${practiceId}/locations/${locationId}/agents/${roleKey}/schedules/${scheduleId}/waive`,
      { reason },
    );
    return data.data;
  },

  /** The location's day-one report ("first look"), or null when none has been written yet. */
  getLocationDayOneReport: async (
    practiceId: string,
    locationId: string,
  ): Promise<AgentDayOneReport | null> => {
    const { data } = await apiClient.get<ApiResponse<AgentDayOneReport | null>>(
      `/agency/practices/${practiceId}/locations/${locationId}/day-one-report`,
    );
    return data.data ?? null;
  },

  /** Reset & rehire ONE agent at a location; `confirmName` is the agent's name as typed. */
  resetAgent: async (
    practiceId: string,
    locationId: string,
    roleKey: string,
    confirmName: string,
  ): Promise<AgentResetResult> => {
    const { data } = await apiClient.post<ApiResponse<AgentResetResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/agents/${roleKey}/reset`,
      { confirmName },
    );
    return data.data;
  },

  resetLocationAgents: async (
    practiceId: string,
    locationId: string,
    confirmName: string,
  ): Promise<AgentResetResult> => {
    const { data } = await apiClient.post<ApiResponse<AgentResetResult>>(
      `/agency/practices/${practiceId}/locations/${locationId}/agent-reset`,
      { confirmName },
    );
    return data.data;
  },
};
